"use client";

import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Pencil,
  Plus,
  X,
  RotateCcw,
  Check,
  ListChecks,
  Loader2,
} from "lucide-react";
import { Input } from "src/components/ui/input";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { useTheme } from "next-themes";
import { Slider } from "src/components/ui/slider";
import axios from "axios";
import { toast } from "sonner";
import { BaseUrlApi } from "../../../lib/api";
import { Skeleton } from "src/components/ui/skeleton";
import { Tooltip } from "antd";

export default function SubTaskCard({ taskId, handleFetchTasks }) {
  const currentTaskId = taskId || "1";

  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const [newTaskName, setNewTaskName] = useState("");
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingSubProgress, setEditingSubProgress] = useState(0);
  const [editingProgress, setEditingProgress] = useState(0);

  const [addSubmitting, setAddSubmitting] = useState(false);
  const [editSubmittingTask, setEditSubmittingTask] = useState(null);
  const [toggleSubmittingTask, setToggleSubmittingTask] = useState(null);
  const [deleteSubmittingTask, setDeleteSubmittingTask] = useState(null);

  const editInputRef = useRef(null);
  const addInputRef = useRef(null);

  useEffect(() => {
    if (isDialogOpen) {
      setIsLoading(true);
      fetchSubTasks();
    }
  }, [isDialogOpen, currentTaskId]);

  useEffect(() => {
    if (editingTaskId && editInputRef.current) {
      editInputRef.current.focus();
    }
  }, [editingTaskId]);

  const fetchSubTasks = async () => {
    try {
      const { data } = await axios.get(
        `${BaseUrlApi}/tasks/${currentTaskId}/sub-tasks`
      );
      setTasks(
        data.data.map((task) => ({
          ...task,
          status: task.status === 1 ? 1 : 0,
        }))
      );
    } catch (error) {
      console.error("Error fetching subtasks:", error);
      toast.error("حدث خطأ أثناء تحميل المهام الفرعية");
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTask = async () => {
    if (newTaskName.trim()) {
      try {
        setAddSubmitting(true);
        const newTask = {
          name: newTaskName.trim(),
          progress: 0,
          status: 0,
          task_id: currentTaskId,
        };

        const { data } = await axios.post(
          `${BaseUrlApi}/tasks/${currentTaskId}/sub-tasks`,
          newTask
        );

        setTasks([
          ...tasks,
          {
            ...data.data,
            status: 0,
          },
        ]);

        setNewTaskName("");
        toast.success("تمت إضافة المهمة الفرعية بنجاح");
        await fetchSubTasks();
        await handleFetchTasks();
        if (addInputRef.current) {
          addInputRef.current.focus();
        }
      } catch (error) {
        console.error("Error adding subtask:", error);
        toast.error("حدث خطأ أثناء إضافة المهمة الفرعية");
      } finally {
        setAddSubmitting(false);
      }
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleAddTask();
    }
  };

  const handleEditKeyDown = (e, taskId) => {
    if (e.key === "Enter") {
      saveEdit(taskId);
    } else if (e.key === "Escape") {
      cancelEdit();
    }
  };

  const startEditing = (task) => {
    setEditingTaskId(task.bid);
    setEditingText(task.name);
    setEditingSubProgress(task.sub_progress);
    setEditingProgress(task.progress);
  };

  const saveEdit = async (subtaskId) => {
    if (editingText.trim()) {
      try {
        setEditSubmittingTask(subtaskId);
        const updatedTask = {
          name: editingText.trim(),
          progress: +editingProgress,
          sub_progress: +editingSubProgress,
          status: editingSubProgress === 100 ? 1 : 0,
          task_id: currentTaskId,
        };

        await axios.put(
          `${BaseUrlApi}/tasks/${currentTaskId}/sub-tasks/${subtaskId}`,
          updatedTask
        );

        setTasks(
          tasks.map((task) =>
            task.bid === subtaskId
              ? {
                  ...task,
                  name: editingText.trim(),
                  progress: +editingProgress,
                  sub_progress: +editingSubProgress,
                  status: editingSubProgress === 100 ? 1 : 0,
                }
              : task
          )
        );

        await fetchSubTasks();
        await handleFetchTasks();
        toast.success("تم تحديث المهمة الفرعية بنجاح");
      } catch (error) {
        console.error("Error updating subtask:", error);
        toast.error("حدث خطأ أثناء تحديث المهمة الفرعية");
      } finally {
        setEditSubmittingTask(null);
        setEditingTaskId(null);
      }
    } else {
      setEditingTaskId(null);
    }
  };

  const cancelEdit = () => {
    setEditingTaskId(null);
  };

  const toggleTaskStatus = async (subtaskId) => {
    const taskToUpdate = tasks.find((task) => task.bid === subtaskId);
    if (!taskToUpdate) return;

    const newStatus = taskToUpdate.status === 0 ? 1 : 0;
    const newProgress = newStatus === 1 ? 100 : taskToUpdate.sub_progress;

    try {
      setToggleSubmittingTask(subtaskId);
      const updatedTask = {
        name: taskToUpdate.name,
        progress: taskToUpdate.progress,
        sub_progress: newProgress,
        status: newStatus,
        task_id: currentTaskId,
      };

      await axios.put(
        `${BaseUrlApi}/tasks/${currentTaskId}/sub-tasks/${subtaskId}`,
        updatedTask
      );

      setTasks(
        tasks.map((task) =>
          task.bid === subtaskId
            ? {
                ...task,
                sub_progress: newProgress,
                status: newStatus,
              }
            : task
        )
      );

      await fetchSubTasks();
      await handleFetchTasks();
      toast.success("تم تحديث حالة المهمة الفرعية");
    } catch (error) {
      console.error("Error toggling subtask status:", error);
      toast.error("حدث خطأ أثناء تحديث حالة المهمة الفرعية");
    } finally {
      setToggleSubmittingTask(null);
    }
  };

  const handleProgressChange = (taskId, newProgress) => {
    const taskToEdit = tasks.find((task) => task.bid === taskId);
    if (!taskToEdit) return;

    setEditingTaskId(taskId);
    setEditingText(taskToEdit.name);
    setEditingSubProgress(newProgress);
  };

  const handleDeleteTask = async (subtaskId) => {
    try {
      setDeleteSubmittingTask(subtaskId);
      await axios.delete(
        `${BaseUrlApi}/tasks/${currentTaskId}/sub-tasks/${subtaskId}`
      );
      setTasks(tasks.filter((task) => task.bid !== subtaskId));
      await fetchSubTasks();
      await handleFetchTasks();
      toast.success("تم حذف المهمة الفرعية بنجاح");
    } catch (error) {
      console.error("Error deleting subtask:", error);
      toast.error("حدث خطأ أثناء حذف المهمة الفرعية");
    } finally {
      setDeleteSubmittingTask(null);
    }
  };

  // useEffect(() => {
  //   function handleClickOutside(event) {
  //     if (
  //       editingTaskId &&
  //       editInputRef.current &&
  //       !editInputRef.current.contains(event.target)
  //     ) {
  //       saveEdit(editingTaskId);
  //     }
  //   }
  //   document.addEventListener("mousedown", handleClickOutside);
  //   return () => {
  //     document.removeEventListener("mousedown", handleClickOutside);
  //   };
  // }, [editingTaskId, editingText, editingProgress]);

  return (
    <>
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogTrigger asChild>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-lg px-2 text-yellow-600"
          >
            <ListChecks />
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle className="text-right">المهام الفرعية</DialogTitle>
          </DialogHeader>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="relative flex-grow">
                <Pencil className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  ref={addInputRef}
                  value={newTaskName}
                  onChange={(e) => setNewTaskName(e.target.value)}
                  placeholder="أضف مهمة جديدة"
                  onKeyDown={handleKeyDown}
                  className="pr-9 text-foreground placeholder:text-muted-foreground rounded-lg h-10 text-right"
                  disabled={addSubmitting}
                />
              </div>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="text-muted-foreground p-2 rounded-lg h-10 w-10 flex items-center justify-center bg-sidebar border"
                onClick={handleAddTask}
                disabled={addSubmitting}
              >
                {addSubmitting ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  <Plus size={18} />
                )}
              </motion.button>
            </div>

            {isLoading ? (
              <div className="space-y-2">
                {Array(2)
                  .fill("")
                  .map((_, index) => (
                    <div
                      key={index}
                      className="flex flex-col gap-2 rounded-md border p-4"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-4 w-4 rounded" />
                          <Skeleton className="h-4 w-[100px]" />
                        </div>
                        <Skeleton className="h-4 w-[70px]" />
                      </div>
                      <Skeleton className="h-2 w-full rounded" />
                    </div>
                  ))}
              </div>
            ) : (
              <AnimatePresence>
                {tasks.map((task) => (
                  <motion.div
                    key={task.bid || task.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2 }}
                    className="mb-3"
                  >
                    {editingTaskId === task.bid ? (
                      <div className="flex flex-col gap-3">
                        <div className="flex items-center justify-between">
                          <Tooltip title="Task Title">
                            <Input
                              ref={editInputRef}
                              value={editingText}
                              onChange={(e) => setEditingText(e.target.value)}
                              onKeyDown={(e) => handleEditKeyDown(e, task.bid)}
                              className="bg-sidebar border-none text-primary rounded-lg flex-grow ml-2 text-right"
                              autoFocus
                              disabled={editSubmittingTask === task.bid}
                            />
                          </Tooltip>
                          <Tooltip title="Progreess">
                            <Input
                              ref={editInputRef}
                              value={editingProgress}
                              onChange={(e) =>
                                setEditingProgress(e.target.value)
                              }
                              onKeyDown={(e) => handleEditKeyDown(e, task.bid)}
                              className="bg-sidebar border-none text-primary rounded-lg flex-grow ml-2 text-right w-1/5"
                              type="number"
                              disabled={editSubmittingTask === task.bid}
                            />
                          </Tooltip>

                          <div className="flex">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={cancelEdit}
                              className="bg-sidebar text-muted-foreground p-2 rounded-r-lg h-10 w-10 flex items-center justify-center"
                              disabled={editSubmittingTask === task.bid}
                            >
                              <X size={16} />
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => saveEdit(task.bid)}
                              className="bg-sidebar text-muted-foreground p-2 rounded-l-lg h-10 w-10 flex items-center justify-center"
                              disabled={editSubmittingTask === task.bid}
                            >
                              {editSubmittingTask === task.bid ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Pencil size={16} />
                              )}
                            </motion.button>
                          </div>
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground text-sm">
                              {editingSubProgress}%
                            </span>
                            <label className="text-sm">نسبة الإنجاز</label>
                          </div>
                          <Slider
                            value={[editingSubProgress]}
                            onValueChange={(value) =>
                              setEditingSubProgress(value[0])
                            }
                            max={100}
                            step={5}
                            className="w-full"
                            disabled={editSubmittingTask === task.bid}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center justify-between">
                          <div className="flex flex-col items-start gap-1">
                            <span
                              className={`${
                                task.status === 1
                                  ? "text-muted-foreground line-through"
                                  : "text-primary"
                              } cursor-pointer`}
                              onClick={() => startEditing(task)}
                            >
                              {task.name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {task.sub_progress}% مكتمل
                            </span>
                          </div>
                          <div className="flex">
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => handleDeleteTask(task.bid)}
                              className="bg-sidebar text-muted-foreground p-2 rounded-r-lg h-10 w-10 flex items-center justify-center"
                              disabled={deleteSubmittingTask === task.bid}
                            >
                              {deleteSubmittingTask === task.bid ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <X size={16} />
                              )}
                            </motion.button>
                            <motion.button
                              whileHover={{ scale: 1.1 }}
                              whileTap={{ scale: 0.9 }}
                              onClick={() => toggleTaskStatus(task.bid)}
                              className="bg-sidebar text-muted-foreground p-2 rounded-l-lg h-10 w-10 flex items-center justify-center"
                              disabled={toggleSubmittingTask === task.bid}
                            >
                              {toggleSubmittingTask === task.bid ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : task.status === 1 ? (
                                <RotateCcw size={16} />
                              ) : (
                                <Check size={16} />
                              )}
                            </motion.button>
                          </div>
                        </div>
                        <div className="w-full mt-1 group relative">
                          <div className="w-full bg-sidebar rounded-full h-1.5">
                            <div
                              className="bg-primary group-hover:opacity-0 h-1.5 rounded-full transition-all duration-300 flex items-center justify-end"
                              style={{ width: `${task.sub_progress}%` }}
                            />
                          </div>
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Slider
                              value={[task.sub_progress]}
                              onValueChange={(value) => {
                                if (
                                  editingTaskId === null &&
                                  !toggleSubmittingTask &&
                                  !deleteSubmittingTask
                                ) {
                                  const newProgress = value[0];
                                  handleProgressChange(task.bid, newProgress);
                                }
                              }}
                              max={100}
                              step={5}
                              className="w-full absolute top-0 cursor-pointer h-1.5"
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            )}

            {!isLoading && tasks.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                لا توجد مهام بعد. أضف مهمة للبدء!
              </div>
            )}
          </motion.div>
        </DialogContent>
      </Dialog>
    </>
  );
}
